import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    type DataTableAction,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { ManagePricesModalProps } from '@/modals/ProductPriceModals/ManagePrices'
import { searchProducts } from '@/modules/Settings/services/product'
import {
    Product,
    ProductGroupWithProducts,
} from '@/modules/Settings/types/product'
import { DollarSign } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ExpandedProductGroup from './ExpandedProductGroup'
import {
    getGroupColumns,
    getProductColumns,
    groupByProductGroup,
} from './product-columns'

const ProductsTab: React.FC = () => {
    const { t } = useTranslation('settings')
    const { setModal } = useModal()

    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams({
            defaults: {
                sort_by: 'product_name',
                sort_order: 'asc' as const,
            },
            prefix: 'prod',
        })

    const apiParams = useMemo(
        () => ({
            q: queryParams.q,
            sort_by: queryParams.sort_by,
            sort_order: queryParams.sort_order,
            page_size: 1000,
        }),
        [queryParams.q, queryParams.sort_by, queryParams.sort_order]
    )

    const {
        data: productsData,
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.PRODUCTS_SEARCH, apiParams],
            queryFn: () => searchProducts(apiParams),
            placeholderData: true,
        },
        { showLoading: true, showError: true }
    )

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handleManagePrices = useCallback(
        (product: Product) => {
            setModal<ManagePricesModalProps>(
                'ProductPriceModals/ManagePrices/index',
                {
                    productId: product.product_id,
                    productName: product.product_name,
                }
            )
        },
        [setModal]
    )

    const allProducts = productsData?.results || []
    const groupedRows = useMemo(
        () => groupByProductGroup(allProducts),
        [allProducts]
    )

    const groupColumns = useMemo(() => getGroupColumns(t), [t])
    const productColumns = useMemo(() => getProductColumns(t), [t])
    const productActions: DataTableAction<Product>[] = useMemo(
        () => [
            {
                key: 'manage-prices',
                label: t('manage-prices'),
                icon: <DollarSign className="w-4 h-4" />,
                variant: 'info',
                onClick: (row) => handleManagePrices(row),
                hidden: (row) => !!row.product_price_model,
            },
        ],
        [t, handleManagePrices]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper>
            <DataTableCustomized<ProductGroupWithProducts>
                columns={groupColumns}
                rows={groupedRows}
                rowKey={(row) => row.product_group_name}
                isFetching={isFetching}
                emptyMessage={t('no-data')}
                sorting={{
                    sortBy: params.sort_by,
                    sortOrder: params.sort_order,
                    onSortChange: handleSortChange,
                }}
                toolbar={{
                    search: {
                        ...searchInputProps,
                        placeholder: t('search-product'),
                    },
                }}
                expandable={{
                    expandedRowRender: (row) => (
                        <ExpandedProductGroup
                            row={row}
                            productColumns={productColumns}
                            productActions={productActions}
                            t={t}
                        />
                    ),
                    rowExpandable: (row) => (row.products?.length || 0) > 0,
                }}
                bordered
            />
        </Paper>
    )
}

export default ProductsTab
